import React, { useState, useContext } from "react";
import { SubscriptionContext } from "components/context/SubscriptionContext";
import { Check, X, Sparkles } from "lucide-react";
import SubscriptionModal from 'pages/Profile/components/SubscriptionModal';
import CancelSubscriptionModal from 'pages/Profile/components/CancelSubscriptionModal';
import toast from 'react-hot-toast';
import { v2SubscriptionsApi } from 'api';
import { logApi } from 'api';

const { createLog } = logApi;

/**
 * PlansDisplay - Core component for displaying subscription plans
 * Can be used in both tab view (as a page) and modal view
 */
const PlansDisplay = ({ 
  showCurrentSubscription = true,
  isModal = false, // Whether this is being used in a modal context
}) => {
  const { currentPlan, currentSubscription, availablePlans, fetchSubscriptionData } = useContext(SubscriptionContext);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const handleSuccess = async (_subscriptionId, message) => {
    await fetchSubscriptionData();
    toast.success(message);
    setShowSubscriptionModal(false);
  };

  const handleError = (message) => {
    toast.error(message);
  };

  const handleSubscribe = (plan) => {
    createLog({
      level: 'info',
      message: `Click en plan de suscripción ${plan.reason} en PlansDisplay`
    });
    
    if (plan.type === 'default_free') {
      return;
    }
    
    if (plan.type === 'default_growth') {
      window.open('https://wa.me/59899860399', '_blank');
      return;
    }

    // Always open the subscription modal
    setSelectedPlan(plan);
    setShowSubscriptionModal(true);
  };

  const getBadgeStatus = (subscription) => {
    if (subscription.status === 'active') return 'Activo';
    if (subscription.status === 'pending_payment') return 'Pendiente de pago';
    if (subscription.current_status === 'active_until_next_payment') return `Cancelada - Activa hasta ${new Date(subscription.next_payment_date).toLocaleDateString()}`;
    return 'Cancelado';
  }

  const handleResumeSubscription = async () => {
    setShowSubscriptionModal(false);
    try {
      await v2SubscriptionsApi.resumeSubscription(currentSubscription.id);
      await fetchSubscriptionData();
      toast.success('Suscripción reactivada con éxito');
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Error al reactivar la suscripción');
    }
  };

  const handlePauseSubscription = async () => {
    setIsCanceling(true);
    try {
      await v2SubscriptionsApi.pauseSubscription(currentSubscription.id);
      await fetchSubscriptionData();
      toast.success('Suscripción cancelada con éxito');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Error al cancelar la suscripción');
    } finally {
      setShowSubscriptionModal(false);
      setIsCanceling(false);
    }
  };

  return (
    <>
      {/* Plans Grid - Moved to top */}
      <div className={`grid gap-4 mb-6 ${
        isModal 
          ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
          : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
      }`}>
        {availablePlans.map((plan) => (
          <div
            key={plan.id}
            className={`backdrop-blur-md border rounded-xl p-4 flex flex-col h-full relative transition-all duration-200 ${
              currentPlan.id === plan.id
                ? "border-blue-400/50 bg-blue-500/20 shadow-lg"
                : "border-white/20 bg-white/10 hover:border-blue-400/50 hover:bg-white/15"
            } ${currentPlan.id !== plan.id && plan.type !== 'default_free' && plan.type !== 'default_growth' ? "cursor-pointer" : ""}`}
            onClick={() => currentPlan.id !== plan.id && plan.type !== 'default_free' && plan.type !== 'default_growth' && handleSubscribe(plan)}
          >
            {currentPlan.id === plan.id && (
              <div className="absolute -top-2 right-4 bg-green-500 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                <Check className="w-3 h-3" />
                <span>Plan Actual</span>
              </div>
            )}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <div>
                  <h4 className="font-semibold text-lg text-center text-white">{plan.reason || `Plan ${plan.id}`}</h4>
                  <div className="flex items-center gap-2 text-center my-auto justify-center">
                    <p className="text-2xl font-bold text-white">
                      {plan.type === "default_growth"
                        ? "Contactar"
                        : `${plan.display_price}`}
                      <span className="text-sm font-normal text-white/60">
                        {plan.type !== "default_growth" && plan.display_frequency}
                      </span>
                    </p>
                    {plan?.free_trial?.frequency && currentPlan.id !== plan.id && (
                      <div className="bg-green-500/20 border border-green-400/30 rounded-lg px-2 py-1 backdrop-blur-md">
                        <div className="flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-green-300" />
                          <span className="text-green-300 text-xs font-medium">¡{plan?.free_trial?.frequency} {plan?.free_trial?.frequency_type === 'days' ? 'días' : 'meses'} gratis!</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {plan.pill && (
                    <div className="w-full flex justify-center mt-2 mb-2">
                      <span className="block rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 px-4 py-1 text-xs font-semibold text-center backdrop-blur-md">
                        {plan.pill}
                      </span>
                    </div>
                  )}
                  {plan.description && (
                    <div className="text-sm text-white/80 mt-2 mb-2">
                      {plan.description}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-grow space-y-3">
              <div>
                <ul className="space-y-2">
                  {plan.features?.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0">
                        <Check className="w-4 h-4 text-green-300 mt-0.5" />
                      </div>
                      <span className="text-sm text-white/80 ml-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {plan?.limitations?.length > 0 && (
                <div>
                  <ul className="space-y-2">
                    {plan?.limitations?.map((limitation, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <X className="w-4 h-4 text-red-300 mt-0.5" />
                        </div>
                        <span className="text-sm text-white/60 ml-2">
                          {limitation}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-4">
              {currentSubscription?.plan_id === plan.id && currentSubscription.status === 'active' && plan.type !== "default_free" ? (
                <button
                  onClick={(e) => {
                    createLog({
                      level: 'info',
                      message: 'Click en "Cancelar Suscripción" en PlansDisplay'
                    });
                    e.stopPropagation();
                    setShowCancelModal(true);
                  }}
                  className="w-full backdrop-blur-md bg-white/10 text-white/90 py-2 px-4 rounded-xl hover:bg-white/15 transition-all border border-white/20 hover:border-red-400/50"
                >
                  {'Cancelar Suscripción'}
                </button>
              ) : currentSubscription?.plan_id === plan.id && currentSubscription.status === 'paused' && plan.type !== "default_free" ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResumeSubscription(plan);
                  }}
                  className="w-full backdrop-blur-md bg-white/10 text-white/90 py-2 px-4 rounded-xl hover:bg-white/15 transition-all border border-white/20 hover:border-green-400/50"
                >
                  {'Reactivar Suscripción'}
                </button>
              ) : currentPlan.id !== plan.id && plan.type !== "default_free" && (
                <>
                  {plan.type === "default_growth" ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://wa.me/59899860399', '_blank');
                      }}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    >
                      Contáctanos
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubscribe(plan);
                      }}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    >
                      {plan.button_text || '¡Empezá a vender!'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Current Subscription Status - Moved below plans */}
      {showCurrentSubscription && currentSubscription && (
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl shadow-lg p-4 md:p-6 z-10">
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-white mb-4">
            Tu Plan Actual
          </h3>
          <div className="space-y-3 md:space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <span className="text-sm md:text-base text-white/70">
                Plan:
              </span>
              <span className="text-sm md:text-base font-medium text-white">
                {currentPlan?.reason || `Plan ${currentPlan?.id}`}
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <span className="text-sm md:text-base text-white/70">
                Fecha de Inicio:
              </span>
              <span className="text-sm md:text-base font-medium text-white">
                {new Date(currentSubscription.start_date).toLocaleDateString()}
              </span>
            </div>
            {currentSubscription.next_payment_date && (
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-sm md:text-base text-white/70">
                  Próximo pago:
                </span>
                <span className="text-sm md:text-base font-medium text-white">
                  {new Date(currentSubscription.next_payment_date).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm md:text-base text-white/70">
                Estado:
              </span>
              <span className="px-3 py-1 bg-green-500/20 border border-green-400/30 text-green-300 rounded-full text-xs md:text-sm backdrop-blur-md">
                {getBadgeStatus(currentSubscription)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
        }}
        onSuccess={handleSuccess}
        onError={handleError}
        plan={selectedPlan}
      />
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setIsCanceling(false);
        }}
        onCancel={handlePauseSubscription}
        loading={isCanceling}
      />
    </>
  );
};

export default PlansDisplay; 